import React, { useState, useEffect, useContext } from 'react';
import { Card, Switch, TextField, Button, Typography, Box, CircularProgress, FormGroup, FormControlLabel, Checkbox, Alert } from '@mui/material';
import API from '../api2';
import useNotification from '../Hooks/useNotification';
import UserContext from './UserSettings/UserContext';

interface UserData {
    first_name: string;
    last_name: string;
    email: string;
    mode: string;
    panelSettings?: string[];
}

// Helper for localStorage background toggle
const getInitialBgEnabled = () => {
    const stored = localStorage.getItem('backgroundEnabled');
    if (stored === null) return true; // default ON
    return stored === 'true';
};

const AccountSettings: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [darkMode, setDarkMode] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    const [backgroundEnabled, setBackgroundEnabled] = useState(getInitialBgEnabled());
    const [panelSettings, setPanelSettings] = useState<string[]>([]);
    const { user, setUser } = useContext(UserContext) || { user: null };
    const notify = useNotification();

    const availablePanelOptions = [
        { key: 'plenumTemp', label: 'Plenum Temperature' },
        { key: 'plenumHumid', label: 'Plenum Humidity' },
        { key: 'co2Level', label: 'CO₂ Level' },
        { key: 'outsideTemp', label: 'Outside Temperature' },
        { key: 'plenumTempSetpoint', label: 'Set Point' },
    ];

    useEffect(() => {
        // Sync background toggle from localStorage on mount
        setBackgroundEnabled(getInitialBgEnabled());
        const fetchUserData = async () => {
            setLoading(true);
            const response = await API.getUserData(user);

            if (response.error) {
                notify(response.message, "error");
            } else {
                setUserData(response.data);
                setCurrentEmail(response.data.email);
                if (response.data.panelSettings) {
                    setPanelSettings(response.data.panelSettings);
                } else {
                    setPanelSettings(['plenumTemp', 'plenumHumid', 'co2Level']);
                }
                
                // Prefer localStorage, fallback to API/user context
                let mode = response.data.mode;
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    try {
                        const parsed = JSON.parse(storedUser);
                        if (parsed.mode) mode = parsed.mode;
                    } catch {}
                }
                setDarkMode(mode === 'dark');
            }
            setLoading(false);
        };

        fetchUserData();
    }, []);

    const handlePanelSettingToggle = (key: string) => {
        setPanelSettings(prev => {
            if (prev.includes(key)) {
                return prev.filter(k => k !== key);
            } else {
                if (prev.length >= 4) return prev;
                return [...prev, key];
            }
        });
    };

    const savePanelSettings = async () => {
        if (panelSettings.length < 2 || panelSettings.length > 4) {
            notify("Please select between 2 and 4 options.", "warning");
            return;
        }

        try {
            const response = await API.request('user/panel-settings', { email: currentEmail, settings: panelSettings }, 'patch');
            if (!response.error) {
                notify("Panel settings saved successfully", "success");
                if (setUser && user) {
                    setUser({ ...user, panelSettings: response.data });
                }
            } else {
                notify(response.message, "error");
            }
        } catch (error) {
            console.error(error);
            notify("Failed to save settings", "error");
        }
    };

        const handleDarkModeToggle = () => {
        const newDarkMode = !darkMode;
        const newMode = newDarkMode ? 'dark' : 'light';
        setDarkMode(newDarkMode);
        if (setUser) {
            setUser({ ...user, mode: newMode });
        }
        // Update localStorage safely - preserve existing user data
        const existingUser = localStorage.getItem('user');
        if (existingUser) {
            try {
                const storedUser = JSON.parse(existingUser);
                storedUser.mode = newMode;
                localStorage.setItem('user', JSON.stringify(storedUser));
            } catch (error) {
                console.error('Error updating user mode in localStorage:', error);
            }
        }

        // Update userData state
        if (userData) {
            setUserData({
                ...userData,
                mode: newMode
            });
        }

        // TODO: Implement actual dark mode functionality with API call
    };

    const handleBackgroundToggle = () => {
        const newEnabled = !backgroundEnabled;
        setBackgroundEnabled(newEnabled);
        localStorage.setItem('backgroundEnabled', String(newEnabled));
        // Trigger a custom event so App.tsx can react immediately
        try {
            const ev = new CustomEvent('backgroundToggle', { detail: { key: 'backgroundEnabled', value: String(newEnabled) } });
            window.dispatchEvent(ev);
        } catch (err) {
            // Fallback: write to localStorage (already done) — storage event won't fire in same tab
        }
    };

    const handleEmailChange = () => {
        // TODO: Implement email change functionality with API call
        console.log('Changing email to:', newEmail);
        setCurrentEmail(newEmail);
        setNewEmail('');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 2 }}>
            <Card sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Account Settings
                </Typography>

                {userData && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Typography variant="h6">
                            {userData.first_name} {userData.last_name}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Appearance
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {/* Dark Mode Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                                checked={darkMode}
                                onChange={handleDarkModeToggle}
                                color="primary"
                            />
                            <Typography>
                                Dark Mode: {darkMode ? 'Enabled' : 'Disabled'}
                            </Typography>
                        </Box>
                        {/* Background Image Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                                checked={backgroundEnabled}
                                onChange={handleBackgroundToggle}
                                color="secondary"
                            />
                            <Typography>
                                Background: {backgroundEnabled ? 'On' : 'Off'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Panel Status Display
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select 2-4 items to display on the live panel status card.
                    </Typography>
                    <FormGroup row sx={{ mt: 1 }}>
                        {availablePanelOptions.map((option) => (
                            <FormControlLabel
                                key={option.key}
                                control={
                                    <Checkbox
                                        checked={panelSettings.includes(option.key)}
                                        onChange={() => handlePanelSettingToggle(option.key)}
                                        disabled={!panelSettings.includes(option.key) && panelSettings.length >= 4}
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </FormGroup>
                    <Button 
                        variant="contained" 
                        onClick={savePanelSettings}
                        sx={{ mt: 2 }}
                    >
                        Save Panel Settings
                    </Button>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Email Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current email: {currentEmail}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                            label="New Email"
                            variant="outlined"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            onClick={handleEmailChange}
                            disabled={!newEmail}
                        >
                            Update Email
                        </Button>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

export default AccountSettings; 